import React, { useState, useMemo } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import { useFinancialHistory } from "./hooks/useFinancialHistory";
import { useUser } from "hooks/useUser";
import LoadingSpinner from "components/LoadingSpinner/LoadingSpinner";
import { Card, CardContent, CardHeader, CardTitle } from "components/ui/card";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "components/ui/dialog";
import { Plus, Edit2, Trash2, Lock, TrendingUp } from "lucide-react";
import toast from "react-hot-toast";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

const formatCurrency = (value) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(
    value || 0
  );

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const total = payload.reduce((sum, entry) => sum + entry.value, 0);
    return (
      <div className="bg-background/95 backdrop-blur-sm border rounded-lg shadow-lg p-4 text-sm min-w-[200px]">
        <p className="font-bold text-foreground mb-3 border-b pb-2">{label}</p>
        <div className="space-y-2">
          {payload.map((entry, index) => (
            <div key={index} className="flex justify-between items-center gap-4">
              <span style={{ color: entry.color }} className="font-medium">{entry.name}:</span>
              <span style={{ color: entry.color }}>{formatCurrency(entry.value)}</span>
            </div>
          ))}
        </div>
        <div className="mt-3 pt-3 border-t flex justify-between items-center font-bold text-primary">
          <span>Total:</span>
          <span>{formatCurrency(total)}</span>
        </div>
      </div>
    );
  }
  return null;
};

const FinancialHistoryTab = () => {
  const { history, isLoading, error, saveDayRecord, deleteDayRecord, closeSeason, fetchSeasons } = useFinancialHistory();
  const { isAdmin } = useUser();
  const [editingRecord, setEditingRecord] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isResetModalOpen, setIsResetModalOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  
  const [seasons, setSeasons] = useState([]);
  const [selectedSeason, setSelectedSeason] = useState("atual");
  const [seasonName, setSeasonName] = useState("");
  const [chartView, setChartView] = useState("monthly");

  React.useEffect(() => {
    const loadSeasons = async () => {
      const result = await fetchSeasons();
      setSeasons(result);
    };
    loadSeasons();
  }, [fetchSeasons]);

  const displayedHistory = useMemo(() => {
    return selectedSeason === "atual" 
      ? history 
      : seasons.find(s => s.id === selectedSeason)?.records || [];
  }, [selectedSeason, history, seasons]);

  const handleOpenEdit = (record = null) => {
    if (record) {
      setEditingRecord({
        isNew: false,
        dateStr: record.id,
        entradas: record.entradas ?? 0,
        brinquedos: record.brinquedos ?? 0,
      });
    } else {
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, "0");
      const day = String(now.getDate()).padStart(2, "0");
      const today = `${year}-${month}-${day}`;

      setEditingRecord({
        isNew: true,
        dateStr: today,
        entradas: 0,
        brinquedos: 0,
      });
    }
    setIsModalOpen(true);
  };

  const handleDateChange = (newDate) => {
    const existing = history.find(h => h.id === newDate);
    setEditingRecord(prev => ({
      ...prev,
      dateStr: newDate,
      entradas: existing ? (existing.entradas ?? 0) : prev.entradas,
      brinquedos: existing ? (existing.brinquedos ?? 0) : prev.brinquedos,
    }));
  };

  const handleSaveRecord = async () => {
    if (!editingRecord?.dateStr) return toast.error("Data é obrigatória.");
    
    // Calcula o total_geral se já existir chalés, caso contrário, só entradas + brinquedos
    const existingDoc = history.find(h => h.id === editingRecord.dateStr);
    const chalesTotal = existingDoc?.chales?.total || 0;
    const entradas = parseFloat(editingRecord.entradas) || 0;
    const brinquedos = parseFloat(editingRecord.brinquedos) || 0;
    const totalGeral = chalesTotal + entradas + brinquedos;

    const payload = {
      date: editingRecord.dateStr,
      entradas,
      brinquedos,
      total_geral: totalGeral,
    };

    if (existingDoc?.chales) {
      payload.chales = existingDoc.chales;
    }

    const success = await saveDayRecord(editingRecord.dateStr, payload);

    if (success) setIsModalOpen(false);
  };


  // Preparar dados para o gráfico
  const chartData = useMemo(() => {
    // Pega os registros ordenados crescentemente
    const sorted = [...displayedHistory].sort((a, b) => a.date.localeCompare(b.date));
    
    if (chartView === "daily") {
      return sorted.slice(-30).map(d => ({
        name: d.date.split('-').reverse().slice(0, 2).join('/'), // DD/MM
        Chalés: d.chales?.total || 0,
        Entradas: d.entradas || 0,
        Brinquedos: d.brinquedos || 0,
      }));
    } else {
      // Agrupamento Mensal
      const monthly = {};
      sorted.forEach(d => {
        const [year, month] = d.date.split('-');
        const key = `${month}/${year}`;
        if (!monthly[key]) {
          monthly[key] = { name: key, Chalés: 0, Entradas: 0, Brinquedos: 0 };
        }
        monthly[key].Chalés += (d.chales?.total || 0);
        monthly[key].Entradas += (d.entradas || 0);
        monthly[key].Brinquedos += (d.brinquedos || 0);
      });
      return Object.values(monthly);
    }
  }, [displayedHistory, chartView]);

  if (isLoading) return <LoadingSpinner />;
  if (error) return <p className="text-red-500">{error}</p>;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 border-b pb-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-foreground">
            Histórico Financeiro
          </h2>
          <p className="text-muted-foreground mt-1 text-sm">Consolidação diária de receitas.</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto items-start sm:items-center">
          <Select value={selectedSeason} onValueChange={setSelectedSeason}>
            <SelectTrigger className="w-full sm:w-[250px]">
              <SelectValue placeholder="Selecione a Temporada" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="atual">Temporada Atual (Em Andamento)</SelectItem>
              {seasons.map(season => (
                <SelectItem key={season.id} value={season.id}>
                  {season.nome}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {isAdmin && (
            <div className="flex gap-2 w-full sm:w-auto flex-wrap">
              <Button variant="secondary" className="flex-1 sm:flex-none" onClick={() => toast("Não temos temporadas suficientes para comparar.", { icon: "📊" })}>
                <TrendingUp className="w-4 h-4 mr-2" /> Comparar Temporadas
              </Button>
              {selectedSeason === "atual" && (
                <>
                  <Button variant="destructive" className="flex-1 sm:flex-none" onClick={() => setIsResetModalOpen(true)}>
                    <Lock className="w-4 h-4 mr-2" /> Zerar / Nova Temporada
                  </Button>
                  <Button className="flex-1 sm:flex-none" onClick={() => handleOpenEdit()}>
                    <Plus className="w-4 h-4 mr-2" /> Novo Registro
                  </Button>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {chartData.length > 0 && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-lg sm:text-xl">
              Evolução de Receitas {chartView === "daily" ? "(Diário)" : "(Mensal)"}
            </CardTitle>
            <Select value={chartView} onValueChange={setChartView}>
              <SelectTrigger className="w-[120px] sm:w-[150px]">
                <SelectValue placeholder="Visualização" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="daily">Diária</SelectItem>
                <SelectItem value="monthly">Mensal</SelectItem>
              </SelectContent>
            </Select>
          </CardHeader>
          <CardContent>
            {/* Scroll Horizontal no Mobile com largura flexível */}
            <div className="w-full overflow-x-auto pb-4 hide-scrollbar">
              <div style={{ minWidth: chartView === "daily" ? `${Math.max(500, chartData.length * 40)}px` : "100%" }} className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.5} vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} tickMargin={10} axisLine={false} tickLine={false} />
                    <YAxis 
                      tickFormatter={(val) => `R$ ${val / 1000}k`} 
                      tick={{ fontSize: 12 }} 
                      axisLine={false} 
                      tickLine={false} 
                    />
                    <Tooltip content={<CustomTooltip />} cursor={{ fill: 'var(--muted)', opacity: 0.2 }} />
                    <Legend wrapperStyle={{ paddingTop: '10px' }} />
                    <Bar dataKey="Chalés" stackId="a" fill="#15803d" radius={[0, 0, 4, 4]} isAnimationActive={false} />
                    <Bar dataKey="Entradas" stackId="a" fill="#3b82f6" isAnimationActive={false} />
                    <Bar dataKey="Brinquedos" stackId="a" fill="#f59e0b" radius={[4, 4, 0, 0]} isAnimationActive={false} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Lista Mobile */}
      <div className="block md:hidden space-y-4">
        {displayedHistory.map((record) => (
          <Card key={record.id}>
            <CardContent className="p-4 flex flex-col gap-3">
              <div className="flex justify-between items-center border-b pb-2">
                <span className="font-bold text-lg">
                  {new Date(record.date + "T00:00:00").toLocaleDateString("pt-BR")}
                </span>
                {isAdmin && selectedSeason === "atual" && (
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => handleOpenEdit(record)}>
                      <Edit2 className="w-4 h-4 text-primary" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => deleteDayRecord(record.id)}>
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="flex flex-col">
                  <span className="text-muted-foreground">Chalés</span>
                  <span className="font-semibold">{formatCurrency(record.chales?.total || 0)}</span>
                </div>
                <div className="flex flex-col">
                  <span className="text-muted-foreground">Entradas</span>
                  <span className="font-semibold">{formatCurrency(record.entradas || 0)}</span>
                </div>
                <div className="flex flex-col">
                  <span className="text-muted-foreground">Brinquedos</span>
                  <span className="font-semibold">{formatCurrency(record.brinquedos || 0)}</span>
                </div>
                <div className="flex flex-col bg-primary/10 p-2 rounded">
                  <span className="text-primary font-bold">Total Geral</span>
                  <span className="font-bold text-primary">{formatCurrency(record.total_geral || 0)}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
        {displayedHistory.length === 0 && (
          <p className="text-center text-muted-foreground py-8">Nenhum registro encontrado.</p>
        )}
      </div>

      {/* Tabela Desktop */}
      <div className="hidden md:block bg-white dark:bg-card rounded-lg shadow border overflow-hidden">
        <table className="w-full text-sm text-left">
          <thead className="text-xs uppercase bg-gray-50 dark:bg-muted text-muted-foreground">
            <tr>
              <th className="px-4 py-3">Data</th>
              <th className="px-4 py-3 text-right">Chalés</th>
              <th className="px-4 py-3 text-right">Entradas</th>
              <th className="px-4 py-3 text-right">Brinquedos</th>
              <th className="px-4 py-3 text-right text-primary font-bold">Total Geral</th>
              {isAdmin && selectedSeason === "atual" && <th className="px-4 py-3 text-center">Ações</th>}
            </tr>
          </thead>
          <tbody>
            {displayedHistory.map((record) => (
              <tr key={record.id} className="border-b last:border-b-0 hover:bg-muted/50">
                <td className="px-4 py-3 font-medium">
                  {new Date(record.date + "T00:00:00").toLocaleDateString("pt-BR")}
                </td>
                <td className="px-4 py-3 text-right">{formatCurrency(record.chales?.total || 0)}</td>
                <td className="px-4 py-3 text-right">{formatCurrency(record.entradas || 0)}</td>
                <td className="px-4 py-3 text-right">{formatCurrency(record.brinquedos || 0)}</td>
                <td className="px-4 py-3 text-right font-bold text-primary">{formatCurrency(record.total_geral || 0)}</td>
                {isAdmin && selectedSeason === "atual" && (
                  <td className="px-4 py-3 text-center">
                    <Button variant="ghost" size="icon" onClick={() => handleOpenEdit(record)}>
                      <Edit2 className="w-4 h-4 text-primary" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => deleteDayRecord(record.id)}>
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  </td>
                )}
              </tr>
            ))}
            {displayedHistory.length === 0 && (
              <tr>
                <td colSpan="6" className="text-center py-8 text-muted-foreground">
                  Nenhum registro encontrado.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Modal Edição / Inserção */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="sm:max-w-[425px] h-[100dvh] sm:h-auto flex flex-col">
          <DialogHeader>
            <DialogTitle>
              {editingRecord?.isNew ? "Novo Registro Financeiro" : "Editar Registro Financeiro"}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 space-y-4 py-4 overflow-y-auto">
            <div className="space-y-2">
              <Label>Data</Label>
              <Input
                type="date"
                value={editingRecord?.dateStr || ""}
                onChange={(e) => handleDateChange(e.target.value)}
                disabled={!editingRecord?.isNew}
              />
              {!editingRecord?.isNew && (
                <p className="text-xs text-muted-foreground">
                  A data não pode ser alterada na edição. Para registrar outra data, use "+ Novo Registro".
                </p>
              )}
            </div>

            {/* Resumo da data selecionada */}
            {editingRecord?.dateStr && (() => {
              const existing = history.find(h => h.id === editingRecord.dateStr);
              if (!existing) return null;
              const chalesTotal = existing.chales?.total || 0;
              return (
                <div className="p-3 bg-muted/60 border rounded-lg text-xs space-y-1.5">
                  <div className="font-semibold flex items-center gap-1.5 text-foreground">
                    <span className="inline-block w-2 h-2 rounded-full bg-blue-500" />
                    Registro existente em {new Date(editingRecord.dateStr + "T00:00:00").toLocaleDateString("pt-BR")}:
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-muted-foreground pt-1">
                    <div>Chalés: <strong className="text-foreground">{formatCurrency(chalesTotal)}</strong></div>
                    <div>Entradas: <strong className="text-foreground">{formatCurrency(existing.entradas || 0)}</strong></div>
                    <div>Brinquedos: <strong className="text-foreground">{formatCurrency(existing.brinquedos || 0)}</strong></div>
                    <div>Total Geral: <strong className="text-foreground">{formatCurrency(existing.total_geral || 0)}</strong></div>
                  </div>
                  {chalesTotal > 0 ? (
                    <p className="text-[11px] text-muted-foreground italic pt-0.5">
                      O valor de Chalés será mantido e somado às Entradas e Brinquedos no Total Geral.
                    </p>
                  ) : (
                    <p className="text-[11px] text-muted-foreground italic pt-0.5">
                      Nenhum pagamento de chalé registrado nesta data. O total será a soma de Entradas e Brinquedos.
                    </p>
                  )}
                </div>
              );
            })()}

            <div className="space-y-2">
              <Label>Entradas (R$)</Label>
              <Input
                type="number"
                step="0.01"
                placeholder="0.00"
                value={editingRecord?.entradas ?? ""}
                onChange={(e) => setEditingRecord({...editingRecord, entradas: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label>Brinquedos (R$)</Label>
              <Input
                type="number"
                step="0.01"
                placeholder="0.00"
                value={editingRecord?.brinquedos ?? ""}
                onChange={(e) => setEditingRecord({...editingRecord, brinquedos: e.target.value})}
              />
            </div>
            <div className="p-3 bg-primary/10 rounded-md text-xs text-primary">
              💡 Os pagamentos de Chalés são integrados automaticamente pelo botão "Lançar no Histórico" na aba de Chalés.
            </div>
          </div>
          <DialogFooter className="mt-auto pb-safe">
            <Button variant="outline" onClick={() => setIsModalOpen(false)}>Cancelar</Button>
            <Button onClick={handleSaveRecord}>Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Modal Confirmação de Zerar */}
      <Dialog open={isResetModalOpen} onOpenChange={setIsResetModalOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="text-destructive flex items-center gap-2">
              <Lock className="w-5 h-5" /> Zerar / Nova Temporada
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <p className="text-sm text-muted-foreground">
              Esta ação irá salvar as informações atuais no histórico e <strong>zerar todos os {history.length} registros</strong> de fluxo de caixa atual.
            </p>
            <p className="text-sm text-destructive font-bold">
              Essa operação não pode ser desfeita. Use apenas no início de uma nova temporada.
            </p>
            <div className="grid gap-2 mt-4">
              <Label htmlFor="nomeTemporada">Nome da Temporada que está se encerrando</Label>
              <Input
                id="nomeTemporada"
                placeholder="Ex: Temporada de Inverno 2026"
                value={seasonName}
                onChange={(e) => setSeasonName(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setIsResetModalOpen(false)} disabled={isClosing}>Cancelar</Button>
            <Button variant="destructive" disabled={isClosing} onClick={async () => {
              if (!seasonName.trim()) {
                toast.error("Insira o nome da temporada.");
                return;
              }
              setIsClosing(true);
              const success = await closeSeason(seasonName);
              setIsClosing(false);
              if (success) {
                setIsResetModalOpen(false);
                setSeasonName("");
                const result = await fetchSeasons();
                setSeasons(result);
                setSelectedSeason("atual");
              }
            }}>
              {isClosing ? "Processando..." : "Confirmar e Zerar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FinancialHistoryTab;
